import { Link, useLocation } from "@remix-run/react";
import { BookOpen, List, Plus, Shapes, Settings, Users } from "lucide-react";
import clsx from "clsx";

import { useHwaPromoPip } from "~/features/hwa-access/hooks/use-hwa-promo-pip";
import { useRecoverableRecordings } from "~/hooks/media/use-recoverable-recordings";
import { useIsPractitioner } from "~/lib/user";

interface MobileTab {
    icon: typeof List;
    label: string;
    path: string;
    matchPaths: string[];
    attention?: boolean;
}

// AIDEV-NOTE: Mobile-only bottom tab bar for app-like navigation on mobile devices
export function MobileTabBar() {
    const location = useLocation();
    const { isPractitioner } = useIsPractitioner();
    // AIDEV-NOTE: Orange pip on New Session when a recording is stranded in
    // this browser's IndexedDB awaiting retry (mirrors sidebar-nav).
    const { count: recoverableRecordingsCount } = useRecoverableRecordings();
    const hasUnfinishedRecordings = recoverableRecordingsCount > 0;

    const baseTabs: MobileTab[] = [
        {
            icon: List,
            label: "Sessions",
            path: isPractitioner ? "/practitioner/sessions" : "/sessions",
            matchPaths: ["/sessions", "/practitioner/sessions"],
        },
        {
            icon: Shapes,
            label: "Parts",
            path: "/parts",
            matchPaths: ["/parts"],
        },
        {
            icon: Plus,
            label: "New Session",
            path: "/invite",
            matchPaths: ["/invite"],
            attention: hasUnfinishedRecordings,
        },
    ];

    // AIDEV-NOTE: Add Clients tab only for practitioners, between New Session and Settings
    const practitionerTabs = isPractitioner
        ? [
              {
                  icon: Users,
                  label: "Clients",
                  path: "/practitioner/clients",
                  matchPaths: ["/practitioner/clients"],
              },
          ]
        : [];

    const settingsTab = {
        icon: Settings,
        label: "Settings",
        path: "/account",
        matchPaths: [
            "/account",
            "/settings",
            "/practitioner/preferences",
            "/account/preferences",
        ],
    };

    // AIDEV-NOTE: Resources is hidden for practitioners on mobile ON PURPOSE — practitioners already
    // have 5 tabs (Sessions, Parts, New Session, Clients, Settings) and a 6th doesn't fit the bottom
    // bar. Desktop (sidebar-nav.tsx) has room and shows it to both. Do NOT drop !isPractitioner here.
    const { showPip: showHwaPromoPip } = useHwaPromoPip();

    const resourcesTab = !isPractitioner
        ? [
              {
                  icon: BookOpen,
                  label: "Resources",
                  path: "/resources",
                  matchPaths: ["/resources"],
                  // AIDEV-NOTE: Healing Works Academy launch pip, same one-shot signal
                  // as the desktop sidebar (useHwaPromoPip) — it clears for both the
                  // first time Resources is opened on either.
                  attention: showHwaPromoPip,
              },
          ]
        : [];

    const tabs: MobileTab[] = [
        ...baseTabs,
        ...practitionerTabs,
        ...resourcesTab,
        settingsTab,
    ];

    const isActive = (matchPaths: string[]) => {
        return matchPaths.some((path) => location.pathname.startsWith(path));
    };

    return (
        // AIDEV-NOTE: pb-[env(safe-area-inset-bottom)] prevents home indicator from obscuring tab items on iPhone X+
        <div className="w-screen inset-x-0 bottom-0 z-50 block border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)] lg:hidden print:hidden">
            <nav
                className="mx-auto flex max-w-md items-center justify-around"
                aria-label="Main navigation"
            >
                {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const active = isActive(tab.matchPaths);

                    return (
                        <Link
                            key={tab.path}
                            to={tab.path}
                            aria-label={tab.label}
                            aria-current={active ? "page" : undefined}
                            className={clsx(
                                "flex flex-1 flex-col items-center justify-center gap-1 py-3 transition-colors",
                                active
                                    ? "text-brand-secondary"
                                    : "text-gray-600 hover:text-brand-secondary",
                            )}
                        >
                            <span className="relative">
                                <Icon className="size-6" aria-hidden="true" />
                                {tab.attention === true && (
                                    <span
                                        className="absolute -top-0.5 -right-1 size-2.5 rounded-full bg-brand-terra-600 ring-2 ring-white"
                                        data-testid="nav-attention-pip"
                                        aria-label="Needs attention"
                                    />
                                )}
                            </span>
                            <span className="text-xs font-medium">
                                {tab.label}
                            </span>
                        </Link>
                    );
                })}
            </nav>
        </div>
    );
}
